import { EXPERIMENT_VALIDATION_CONSTANTS } from 'constants/experimentDefaults'
import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme
} from '@mui/material'
import { DeepcellTextField } from 'components/shared'
import { validateWell } from 'utils/experimentValidation'
import { ExperimentData, ExperimentHandlers } from './types'

interface WellsTableProps {
  experimentData: ExperimentData
  handlers: {
    handleWellOutputChange: ExperimentHandlers['handleWellOutputChange']
    handleWellFrequencyChange: ExperimentHandlers['handleWellFrequencyChange']
  }
  theme: Theme
}

interface ValidationErrors {
  [wellId: number]: string | undefined
}

const WellsTable: React.FC<WellsTableProps> = ({ experimentData, handlers, theme }) => {
  // Track validation errors
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})

  // Store refs to all inputs for navigation
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({})

  // Validation function that checks all wells
  // This happens before making an API request, so that we can catch errors here in the frontend
  // and show errors more clearly and quickly in the UX
  // (instead of getting a generic list of errors or warnings back from the backend)
  //
  // Note: This does require keeping validation logic in sync with the backend
  //
  // For now, we've just hardcoded similar constants, but if we care about cohesion more in the future
  // we might want to add a backend API call that fetches experiment constraints from the backend
  // based on Controller version.
  const validateAllWells = useCallback((wells: typeof experimentData.wells) => {
    const errors: ValidationErrors = {}

    wells.forEach((well) => {
      // Only validate rows that have some data
      if (well.outputCells > 0 || well.estimatedFrequency > 0) {
        // Check for incomplete rows
        if (well.outputCells > 0 && well.estimatedFrequency === 0) {
          errors[well.id] = `Well ${well.id}: Missing target frequency`
        } else if (well.outputCells === 0 && well.estimatedFrequency > 0) {
          errors[well.id] = `Well ${well.id}: Missing number of output cells`
        } else if (experimentData.experimentGoal !== 'imaging') {
          // Validate ranges for complete rows (only for sorting modes)
          const wellValidation = validateWell(well, experimentData.experimentGoal)
          if (wellValidation.hasOutputCellsError || wellValidation.hasFrequencyError) {
            // Build appropriate error message
            if (wellValidation.hasOutputCellsError) {
              const maxCells = experimentData.experimentGoal === 'purity'
                ? EXPERIMENT_VALIDATION_CONSTANTS.MAX_CELLS_PER_WELL_PURITY
                : EXPERIMENT_VALIDATION_CONSTANTS.MAX_CELLS_PER_WELL_YIELD
              errors[well.id] = `Well ${well.id}: Maximum ${maxCells.toLocaleString()} cells per well`
            } else if (wellValidation.hasFrequencyError) {
              errors[well.id] = `Well ${well.id}: Target frequency must be greater than ${EXPERIMENT_VALIDATION_CONSTANTS.MIN_TARGET_FREQUENCY}% and less than ${EXPERIMENT_VALIDATION_CONSTANTS.MAX_TARGET_FREQUENCY}%`
            }
          }
        }
      }
    })

    return errors
  }, [experimentData])

  // No debouncing needed - parent handles it

  // Validate when wells data changes
  useEffect(() => {
    const errors = validateAllWells(experimentData.wells)
    setValidationErrors(errors)
  }, [experimentData.wells, validateAllWells])

  // No complex debounced effects needed - parent handles debouncing

  // No local state sync needed - using props directly

  // Get display values from props
  const getOutputValue = (well: typeof experimentData.wells[0]) => 
    well.outputCells === 0 ? '' : well.outputCells.toString()

  const getFrequencyValue = (well: typeof experimentData.wells[0]) => 
    well.estimatedFrequency === 0 ? '' : well.estimatedFrequency.toString()

  // Handle changes by calling parent handlers directly
  const handleOutputChange = (wellId: number, value: string) => {
    handlers.handleWellOutputChange(wellId, value)
  }

  const handleFrequencyChange = (wellId: number, value: string) => {
    handlers.handleWellFrequencyChange(wellId, value)
  }

  // Handle Enter key to move to next input
  const handleKeyDown = (e: React.KeyboardEvent, wellId: number, field: 'output' | 'frequency') => {
    if (e.key === 'Enter') {
      e.preventDefault()

      // Determine next input
      let nextKey: string
      if (field === 'output') {
        // Move to frequency of same well
        nextKey = `frequency-${wellId}`
      } else {
        // Move to output of next well
        const nextWellId = wellId < 6 ? wellId + 1 : 1
        nextKey = `output-${nextWellId}`
      }

      // Focus and select the next input
      const nextInput = inputRefs.current[nextKey]
      if (nextInput) {
        nextInput.focus()
        nextInput.select()
      }
    }
  }

  // Handle click to select all text
  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement
    target.select()
  }

  // Check if a well has an error
  const hasError = (wellId: number): boolean => {
    return !!validationErrors[wellId]
  }

  // Collect all validation errors
  const allErrors = Object.values(validationErrors).filter(Boolean)

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="body2"
        sx={{
          mb: 1,
          fontWeight: 400,
          color: theme.palette.text.primary,
          fontSize: '14px',
          lineHeight: '16px',
        }}
      >
        Cell Counts Per Well
      </Typography>

      {/* Tip after Cell Counts Per Well header */}
      <Typography
        variant="body2"
        sx={{
          fontSize: '14px',
          color: theme.palette.text.secondary,
          mb: 2
        }}
      >
        Reducing the number of sorted cells will reduce output well volume
      </Typography>

      <TableContainer
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  width: '100px'
                }}
              >
                Well
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  width: '200px'
                }}
              >
                Number of output cells needed
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  width: '200px'
                }}
              >
                Estimated frequency of target cells in sample (%)
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {experimentData.wells.map((well) => (
              <TableRow key={well.id}>
                <TableCell sx={{ verticalAlign: 'middle' }}>
                  Well {well.id}
                </TableCell>
                <TableCell sx={{ width: '200px', verticalAlign: 'middle' }}>
                  <DeepcellTextField
                    inputRef={(ref) => { inputRefs.current[`output-${well.id}`] = ref }}
                    value={getOutputValue(well)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleOutputChange(well.id, e.target.value)
                    }
                    onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(e, well.id, 'output')}
                    onClick={handleInputClick}
                    type="number"
                    inputProps={{ min: 0 }}
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-root': {
                        backgroundColor: theme.palette.background.default,
                        height: '36px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: hasError(well.id) ? theme.palette.error.main : '#d0d0d0',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: hasError(well.id) ? theme.palette.error.main : theme.palette.primary.main,
                          borderWidth: '2px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: hasError(well.id) ? theme.palette.error.main : '#d0d0d0',
                        },
                        '& .MuiInputBase-input': {
                          paddingTop: '8px',
                          paddingBottom: '8px',
                          fontSize: '0.875rem',
                          color: theme.palette.text.primary,
                        },
                      },
                    }}
                  />
                </TableCell>
                <TableCell sx={{ width: '200px', verticalAlign: 'middle' }}>
                  <DeepcellTextField
                    inputRef={(ref) => { inputRefs.current[`frequency-${well.id}`] = ref }}
                    value={getFrequencyValue(well)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleFrequencyChange(well.id, e.target.value)
                    }
                    onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(e, well.id, 'frequency')}
                    onClick={handleInputClick}
                    type="number"
                    inputProps={{ step: 'any' }}
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-root': {
                        backgroundColor: theme.palette.background.default,
                        height: '36px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: hasError(well.id) ? theme.palette.error.main : '#d0d0d0',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: hasError(well.id) ? theme.palette.error.main : theme.palette.primary.main,
                          borderWidth: '2px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: hasError(well.id) ? theme.palette.error.main : '#d0d0d0',
                        },
                        '& .MuiInputBase-input': {
                          paddingTop: '8px',
                          paddingBottom: '8px',
                          fontSize: '0.875rem',
                          color: theme.palette.text.primary,
                        },
                      },
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Display validation errors */}
      {allErrors.length > 0 && (
        <Box sx={{ mb: 4, mt: 2 }}>
          {allErrors.map((error) => (
            <Typography
              key={error}
              color="error"
              variant="body2"
              sx={{
                fontSize: '14px',
                lineHeight: '20px',
                mb: 0.5,
              }}
            >
              {error}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default WellsTable